import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Save, Play, CheckCircle, XCircle, FlaskConical, Target } from 'lucide-react';

const TARGETS = [
  { value: 'status', label: 'Status Code' },
  { value: 'response_time', label: 'Response Time (ms)' },
  { value: 'body.message', label: 'body.message' },
  { value: 'body.data', label: 'body.data' },
  { value: 'body.id', label: 'body.id' },
  { value: 'body.success', label: 'body.success' },
  { value: 'body.error', label: 'body.error' },
  { value: 'header.content-type', label: 'header: Content-Type' },
  { value: 'header.x-request-id', label: 'header: X-Request-ID' },
];

const OPERATORS = [
  { value: 'equals',        label: '= equals' },
  { value: 'not_equals',    label: '≠ not equals' },
  { value: 'contains',      label: '⊃ contains' },
  { value: 'not_contains',  label: '⊅ not contains' },
  { value: 'greater_than',  label: '> greater than' },
  { value: 'less_than',     label: '< less than' },
  { value: 'exists',        label: '∃ exists' },
  { value: 'is_empty',      label: '∅ is empty' },
  { value: 'matches_regex', label: '~ matches regex' },
];

export default function AssertionsPanel({ endpointId, lastResponse }) {
  const [name, setName] = useState('Test Suite');
  const [assertions, setAssertions] = useState([]);
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => { if (endpointId) load(); }, [endpointId]);

  async function load() {
    try {
      const res = await api.get(`/endpoints/${endpointId}/assertions`);
      if (res.data.length) {
        const tc = res.data[0];
        setName(tc.name || 'Test Suite');
        setAssertions(tc.assertions || []);
      } else {
        setAssertions([]);
        setName('Test Suite');
      }
      setResults(null);
    } catch {}
  }

  function addAssertion() {
    setAssertions(a => [...a, {
      id: Date.now(),
      type: 'response',
      target: 'status',
      operator: 'equals',
      expected: '200',
      description: '',
    }]);
  }

  function updateAssertion(idx, field, value) {
    setAssertions(a => a.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  }

  function removeAssertion(idx) {
    setAssertions(a => a.filter((_, i) => i !== idx));
    setResults(null);
  }

  async function save() {
    setSaving(true);
    try {
      await api.post(`/endpoints/${endpointId}/assertions`, { name, assertions });
      toast.success('Assertions saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  }

  async function runAll() {
    if (!lastResponse) {
      toast.error('Send a request first to run assertions against the response');
      return;
    }
    setRunning(true);
    try {
      // First save
      await api.post(`/endpoints/${endpointId}/assertions`, { name, assertions });
      // Then run
      const res = await api.post(`/endpoints/${endpointId}/run-assertions`, {
        response_data: {
          status: lastResponse.status,
          body: lastResponse.data,
          headers: lastResponse.headers,
          response_time: lastResponse.response_time,
        }
      });
      setResults(res.data);
    } catch { toast.error('Failed to run assertions'); }
    finally { setRunning(false); }
  }

  if (!endpointId) return (
    <div style={sty.empty}>
      <Target size={36} style={{ opacity: 0.15 }} />
      <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>Select an endpoint to add test assertions</p>
    </div>
  );

  return (
    <div style={sty.root}>
      <div style={sty.header}>
        <div style={sty.headerLeft}>
          <input style={sty.nameInput} value={name} onChange={e => setName(e.target.value)} placeholder="Test Suite Name" />
          <span style={sty.count}>{assertions.length} assertion{assertions.length !== 1 ? 's' : ''}</span>
        </div>
        <div style={sty.headerRight}>
          <button className="btn btn-ghost btn-sm" onClick={addAssertion}>
            <Plus size={13} /> Add Assertion
          </button>
          <button className="btn btn-ghost btn-sm" onClick={save} disabled={saving}>
            <Save size={13} /> {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            style={sty.runBtn}
            onClick={runAll}
            disabled={running || !assertions.length}
            title={!lastResponse ? 'Send a request first' : ''}
          >
            <Play size={13} /> {running ? 'Running...' : 'Run Tests'}
          </button>
        </div>
      </div>

      {/* Results summary */}
      {results && (
        <div style={{ ...sty.resultBanner, background: results.failed === 0 ? 'rgba(48,217,134,0.08)' : 'rgba(240,92,92,0.08)', borderColor: results.failed === 0 ? '#30d98640' : '#f05c5c40' }}>
          <div style={sty.resultSummary}>
            {results.failed === 0
              ? <CheckCircle size={16} style={{ color: 'var(--accent-green)' }} />
              : <XCircle size={16} style={{ color: 'var(--accent-red)' }} />}
            <span style={{ fontWeight: 600, color: results.failed === 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
              {results.failed === 0 ? 'All tests passed!' : `${results.failed} test${results.failed !== 1 ? 's' : ''} failed`}
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              {results.passed}/{results.total} passed
            </span>
          </div>
        </div>
      )}

      {/* Assertions list */}
      <div style={sty.list}>
        {assertions.length === 0 && (
          <div style={sty.emptyList}>
            <FlaskConical size={32} style={{ opacity: 0.15, marginBottom: 10 }} />
            <p style={{ color: 'var(--text-muted)' }}>No assertions yet.</p>
            <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>Click "Add Assertion" to create your first test.</p>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={addAssertion}>
              <Plus size={13} /> Add Assertion
            </button>
          </div>
        )}

        {assertions.map((a, idx) => {
          const result = results?.results?.[idx];
          return (
            <div key={a.id || idx} style={{ ...sty.assertionRow, ...(result ? (result.passed ? sty.rowPassed : sty.rowFailed) : {}) }}>
              {/* Status indicator */}
              <div style={sty.assertionStatus}>
                {result
                  ? result.passed
                    ? <CheckCircle size={15} style={{ color: 'var(--accent-green)' }} />
                    : <XCircle size={15} style={{ color: 'var(--accent-red)' }} />
                  : <div style={sty.statusDot} />}
              </div>

              {/* Target */}
              <div style={sty.assertionField}>
                <label style={sty.fieldLabel}>Target</label>
                <select style={sty.select} value={a.target} onChange={e => updateAssertion(idx, 'target', e.target.value)}>
                  {TARGETS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  <option value={a.target.startsWith('body.') && !TARGETS.find(t => t.value === a.target) ? a.target : '__custom'}>{a.target.startsWith('body.') && !TARGETS.find(t => t.value === a.target) ? a.target : 'Custom...'}</option>
                </select>
                {/* Custom target input */}
              </div>

              {/* Operator */}
              <div style={sty.assertionField}>
                <label style={sty.fieldLabel}>Operator</label>
                <select style={sty.select} value={a.operator} onChange={e => updateAssertion(idx, 'operator', e.target.value)}>
                  {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              {/* Expected */}
              {!['exists', 'is_empty'].includes(a.operator) && (
                <div style={sty.assertionField}>
                  <label style={sty.fieldLabel}>Expected</label>
                  <input
                    style={sty.expectedInput}
                    value={a.expected}
                    onChange={e => updateAssertion(idx, 'expected', e.target.value)}
                    placeholder="e.g. 200"
                    className="mono"
                  />
                </div>
              )}

              {/* Result message */}
              {result && (
                <div style={{ ...sty.resultMsg, color: result.passed ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                  {result.message}
                  {!result.passed && result.actual !== undefined && (
                    <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>
                      (got: <code style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{String(result.actual)}</code>)
                    </span>
                  )}
                </div>
              )}

              <button style={sty.removeBtn} onClick={() => removeAssertion(idx)}><Trash2 size={13} /></button>
            </div>
          );
        })}
      </div>

      {/* Last response info */}
      {!lastResponse && assertions.length > 0 && (
        <div style={sty.noResponseHint}>
          <FlaskConical size={13} style={{ color: 'var(--accent-orange)' }} />
          Send a request first, then click "Run Tests" to validate the response
        </div>
      )}
    </div>
  );
}

const sty = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  empty: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', flexShrink: 0, gap: 12 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12, flex: 1, overflow: 'hidden' },
  nameInput: { fontSize: 15, fontWeight: 600, background: 'transparent', border: 'none', color: 'var(--text-primary)', padding: 0, fontFamily: 'var(--sans)', flex: 1, minWidth: 0 },
  count: { fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 },
  headerRight: { display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' },
  runBtn: { display: 'flex', alignItems: 'center', gap: 5, background: 'var(--accent-green)', color: '#0d1a12', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--sans)', transition: 'all 0.15s' },
  resultBanner: { padding: '10px 16px', border: '1px solid', borderLeft: 'none', borderRight: 'none', flexShrink: 0 },
  resultSummary: { display: 'flex', alignItems: 'center', gap: 10 },
  list: { flex: 1, overflow: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
  emptyList: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', textAlign: 'center', flex: 1 },
  assertionRow: { display: 'flex', alignItems: 'flex-end', gap: 8, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', flexWrap: 'wrap', transition: 'border-color 0.15s' },
  rowPassed: { borderColor: '#30d98640', background: 'rgba(48,217,134,0.04)' },
  rowFailed: { borderColor: '#f05c5c40', background: 'rgba(240,92,92,0.04)' },
  assertionStatus: { display: 'flex', alignItems: 'center', paddingBottom: 6, flexShrink: 0 },
  statusDot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--border-light)', margin: '0 4px' },
  assertionField: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 },
  fieldLabel: { fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  select: { background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)', borderRadius: 5, color: 'var(--text-primary)', fontSize: 12, padding: '5px 7px', cursor: 'pointer', minWidth: 120 },
  expectedInput: { background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)', borderRadius: 5, color: 'var(--text-primary)', fontSize: 12, padding: '5px 7px', minWidth: 100 },
  resultMsg: { fontSize: 11, fontFamily: 'var(--mono)', flex: '1 0 100%', marginTop: 4, padding: '4px 6px', background: 'var(--bg-primary)', borderRadius: 4 },
  removeBtn: { background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', flexShrink: 0, marginLeft: 'auto', alignSelf: 'flex-end' },
  noResponseHint: { display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderTop: '1px solid var(--border)', background: 'rgba(245,166,35,0.06)', fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0 },
};
